import {
  Box,
  Button,
  Container,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import LazyTable, { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import {
  IMediaItem,
  MediaItemSource,
  ListRequestOfProductDeliverableFilter,
  SaveProductDeliverableRequest,
  ProductDeliverableFilter,
  ProductDeliverableInfo,
} from "services/KnowfullyAdminClient";
import {
  DeliverableTypeIds,
  DeliveryMethodSettingCodeConstants,
  Severity,
  SnackbarMessageProps,
} from "types/common-types";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import EditIcon from "@mui/icons-material/Edit";
import { DropResult } from "react-beautiful-dnd";
import { MediaLibraryDialogView } from "pages/MediaLibraryPage/MediaLibraryDialogView";
import { MediaType } from "utils/mediaUtils";
import LinkIcon from "@mui/icons-material/Link";
import HandoutUpdateDialog from "pages/SkusPage/SkuHandouts/HandoutUpdateDialog";
import { calculateSequence } from "helpers/arrayHelpers";
import { HANDOUT_MAX_FILE_SIZE } from "pages/SkusPage/SkuHandouts";

type ProductHandoutsProps = {
  isReadonly?: boolean;
  productId: string;
};

export default function ProductHandouts({
  isReadonly,
  productId,
}: ProductHandoutsProps) {
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const { productDeliverableService } = useGlobalContext();
  const [deleting, setDeleting] = useState<ProductDeliverableInfo>();
  const [editing, setEditing] = useState<ProductDeliverableInfo>();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);
  const [createDialogOpen, createDialogOpenActions] = useBoolean(false);

  const { data, isFetching } = useQuery(
    ["getProductDeliverables", productId],
    () =>
      productDeliverableService.getProductDeliverables(
        productId!,
        ListRequestOfProductDeliverableFilter.fromJS({
          sortField: "sequence",
          parameters: ProductDeliverableFilter.fromJS({
            deliverableTypeIds: [DeliverableTypeIds.Handout],
          }),
        })
      ),
    {
      enabled: !!productId,
    }
  );

  const invalidateQueries = () => {
    queryClient.invalidateQueries(["getProductDeliverables", productId]);
  };

  const ActionsCell = ({ value }: { value: ProductDeliverableInfo }) => {
    return (
      <Box display="flex">
        <IconButton
          size="small"
          color="success"
          onClick={() =>
            window.open(
              value.settingValues?.find(
                (v) =>
                  v.setting?.codeConstant ===
                  DeliveryMethodSettingCodeConstants.Url
              )?.value
            )
          }
        >
          <LinkIcon />
        </IconButton>
        {!isReadonly && (
          <>
            <IconButton size="small" onClick={() => setEditing(value)}>
              <EditIcon />
            </IconButton>
            <IconButton size="small" onClick={() => setDeleting(value)}>
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </Box>
    );
  };

  const handleDelete = async () => {
    if (deleting?.id) {
      await productDeliverableService.deleteProductDeliverable(deleting.id);
      invalidateQueries();
      setDeleting(undefined);
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "handoutDeleted" }), Severity.Warning)
      );
    }
  };

  const handouts = (data?.data ?? []).map((h) => ({ id: h.id!, ...h }));

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const idSequenceMap = calculateSequence(
      handouts,
      result.source.index,
      result.destination.index
    );

    await productDeliverableService.updateProductDeliverableSequence(
      productId,
      idSequenceMap
    );

    invalidateQueries();
  };

  const handleSave = async (libraryItem: IMediaItem) => {
    await productDeliverableService.saveProductDeliverable(
      SaveProductDeliverableRequest.fromJS({
        productId: productId,
        deliverableId: libraryItem.id,
        sequence: handouts.length
          ? Math.max(...handouts.map((h) => h.sequence ?? 0)) + 1
          : 0,
      })
    );

    invalidateQueries();
    createDialogOpenActions.setFalse();

    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "handoutAdded" }), Severity.Success)
    );
  };

  const onHandoutUpdated = () => {
    queryClient.invalidateQueries("getProductDeliverables");

    setEditing(undefined);
    setSnackbarMessage(
      new SnackbarMessageProps(t({ id: "handoutUpdated" }), Severity.Success)
    );
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "displayText",
      type: "text",
      label: t({ id: "displayText" }),
      noSort: true,
    },
    {
      accessor: "description",
      type: "text",
      label: t({ id: "descriptionLabel" }),
      noSort: true,
    },
    {
      accessor: "",
      type: "node",
      label: t({ id: "fileNameLabel" }),
      noSort: true,
      View: ({ value }: { value: ProductDeliverableInfo }) => (
        <>
          {
            value.settingValues?.find(
              (v) =>
                v.setting?.codeConstant ===
                DeliveryMethodSettingCodeConstants.FileName
            )?.value
          }
        </>
      ),
    },
    {
      accessor: "",
      width: 1,
      type: "node",
      View: ActionsCell,
      label: "",
      noSort: true,
    },
  ];

  if (!isReadonly) {
    cells.unshift({
      accessor: "",
      width: 1,
      type: "node",
      View: () => <DragHandleIcon />,
      label: "",
      noSort: true,
    });
  }

  return (
    <Container>
      {!isReadonly && (
        <>
          <Box my={2} textAlign="right">
            <Button
              variant="outlined"
              type="submit"
              color="primary"
              onClick={createDialogOpenActions.setTrue}
            >
              <T id="addHandout" />
            </Button>
          </Box>

          <DeleteEntityDialog
            dialogTitleKey="deleteHandoutFromProduct"
            contentKey="areYouSureToDeleteHandout"
            onCancel={() => setDeleting(undefined)}
            handleDelete={handleDelete}
            open={!!deleting}
          />

          <Dialog
            fullWidth
            maxWidth={"lg"}
            open={createDialogOpen}
            onClose={createDialogOpenActions.setFalse}
          >
            <DialogContent>
              <MediaLibraryDialogView
                source={MediaItemSource.Handouts}
                onClickMediaLibraryItem={handleSave}
                options={[MediaType.DOCUMENT, MediaType.IMAGE, MediaType.ARCHIVE]}
                customBackButtonLabel="backToCourseMaterialsLibrary"
                alternativeTextRequired={false}
                customAlternativeTextLabel="displayText"
                maxFileSizeBytes={HANDOUT_MAX_FILE_SIZE}
              />
            </DialogContent>
          </Dialog>

          <HandoutUpdateDialog
            handoutId={editing?.deliverableId}
            onUpdated={onHandoutUpdated}
            onClose={() => setEditing(undefined)}
          />

          <SnackbarMessage
            snackbarMessage={snackbarMessage}
            setSnackbarMessage={setSnackbarMessage}
          />
        </>
      )}

      <LazyTable
        {...{
          cells,
          rows: handouts,
          rowCount: handouts.length,
          page: 0,
          setPage: () => {},
          rowsPerPage: handouts.length,
          setRowsPerPage: () => {},
          order: "asc",
          setOrder: () => {},
          orderBy: "",
          setOrderBy: () => {},
          loading: isFetching,
          dense: true,
          allowDragAndDrop: !isReadonly,
          onDragAndDropEnd: handleDragEnd,
          allowPagination: false,
        }}
      />
    </Container>
  );
}
